import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from "@angular/core";
import { PluginInjectorWidget } from "core-client/api";
import { SCREEN_DETAILS_SOURCE, toWidgetScreen, WidgetScreen } from "../../services/widget-screen";
import { SharedModule } from "../shared.module";

export type WidgetHeaderVariant = "grid" | "swiper" | "window";

@Component({
  selector: "app-widget-header",
  imports: [SharedModule],
  templateUrl: "./widget-header.component.html",
  styleUrl: "./widget-header.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class WidgetHeaderComponent {
  private readonly screenDetailsSource = inject(SCREEN_DETAILS_SOURCE);
  availableScreens: readonly WidgetScreen[] = [];

  @Input({ required: true })
  widget!: PluginInjectorWidget.IWidget;

  @Input()
  variant: WidgetHeaderVariant = "grid";

  @Input()
  isFullscreen: boolean = false;

  @Input()
  canToggleFullscreen: boolean = false;

  @Input()
  canOpenInWindow: boolean = false;

  @Input()
  canCloseWindow: boolean = false;

  @Input()
  canOpenSettings: boolean = false;

  @Input()
  canRemove: boolean = false;

  @Output()
  readonly fullscreenToggle: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  readonly openInWindow: EventEmitter<WidgetScreen | undefined> =
    new EventEmitter<WidgetScreen | undefined>();

  @Output()
  readonly closeWindow: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  readonly settingsOpen: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  readonly removeRequest: EventEmitter<MouseEvent | TouchEvent> =
    new EventEmitter<MouseEvent | TouchEvent>();

  // Reaching the body-level panel takes the pointer off the widget, so a hover-displayed header
  // would otherwise be removed with the open menu inside it.
  @Output()
  readonly menuOpenChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  async loadScreenDetails(): Promise<void> {
    const source = this.screenDetailsSource;
    if (!source) {
      return;
    }
    try {
      const details = await source();
      this.availableScreens = details.screens.map(toWidgetScreen);
    } catch (error) {
      console.warn(`[WidgetHeader] screen list unavailable: ${(error as Error).message}`);
    }
  }

  get showTitle(): boolean {
    return !!this.widget?.appPluginWidget?.settings?.showWidgetTitle;
  }

  get title(): string {
    return `${this.widget?.name ?? ""}${this.widget?.extraTitle ?? ""}`;
  }

  get actionCount(): number {
    return [
      this.canToggleFullscreen,
      this.canOpenInWindow && !this.isFullscreen,
      this.canCloseWindow,
      this.canOpenSettings,
      this.canRemove,
    ].filter(Boolean).length;
  }

  // a single action is never worth hiding behind a menu — it takes the same space as the menu button
  get isCollapsible(): boolean {
    return this.actionCount > 1;
  }

  get screens(): readonly WidgetScreen[] {
    return this.availableScreens;
  }

  get iconButtonClass(): string {
    return this.variant === "swiper" ? "swiper-icon" : "adx-grid-item-header-icon";
  }

  get faIconClass(): string {
    return this.variant === "swiper" ? "swiper-fa-icon" : "adx-grid-item-fa-icon";
  }

  onRemove(event: MouseEvent | TouchEvent): void {
    this.removeRequest.emit(event);
  }
}
